/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ISchemaValueModel, TGenericBlueprintSchema, TGenericModelNode } from "@adapptio/lib-blueprint";
import { REFERENCE_TYPE } from "../../modules/Core/ReferenceGuide/ReferenceGuideTypes";
import { isInspectorNodeDefault, TInspectorNode, TInspectorNode_ReferenceData } from "../InspectorNodeTypes";

export function shouldPassThrough(model: TGenericModelNode): boolean {
	return model.schema.opts?.editorOptions?.layoutType === "passthrough";
}

export function isHidden(model: TGenericModelNode): boolean {
	return model.schema.opts?.editorOptions?.hidden === true || model.schema.opts?.hidden === true;
}

export function isEmptyNode(node: TInspectorNode): boolean {
	if (!node) {
		return true;
	}

	const emptyLabel = !node.label;
	const emptyChildren = isInspectorNodeDefault(node) && !node.children?.length;
	const emptyControls = !node.controls?.length;

	return emptyLabel && emptyChildren && emptyControls;
}

export function isLabelNode(node: TInspectorNode): boolean {
	if (!node.label) {
		return false;
	}

	const emptyChildren = isInspectorNodeDefault(node) && !node.children?.length;
	const emptyControls = !node.controls?.length;

	return emptyChildren && emptyControls;
}

export function hasExpressionAllowed(model: ISchemaValueModel<TGenericBlueprintSchema>): boolean {
	return model.schema.opts?.allowExpression !== false;
}

export function getParentComponentName(model: TGenericModelNode): TInspectorNode_ReferenceData {
	if (!model.parent) {
		return undefined;
	}

	let componentName = null,
		type = null,
		category = null,
		parent = model.parent;

	while (parent !== null || componentName === null) {
		if (parent?.["componentHandler"]) {
			componentName = parent?.["componentHandler"]?.name;
			type = REFERENCE_TYPE.COMPONETS;
			break;
		} else if (parent?.schema.name === "flowNode" && !model?.["constant"]) {
			componentName = parent?.["type"];
			type = REFERENCE_TYPE.ACTIONS;
			break;
		} else if (parent?.schema.name === "namedConfig") {
			componentName = parent?.["name"];
			category = REFERENCE_TYPE.INTEGRATIONS;
			type = REFERENCE_TYPE.INTEGRATIONS;
			break;
		} else if (parent?.parent) {
			parent = parent.parent;
		} else {
			parent = null;
			componentName = undefined;
			break;
		}
	}

	return {
		name: componentName,
		type: type,
		category: category
	};
}
